"use client";

import { useEffect, useState, type ReactNode } from "react";

import { useStudio } from "@/contexts/studio-context";
import type { Signature } from "@/lib/studio/studio";

interface MetronomePulseDotsProps {
  signature: Signature;
  /**
   * When true, the dot at the current beat lights up. When false the dots
   * stay in their idle/preview state, useful inside a popover that's open
   * while the metronome is off.
   */
  running: boolean;
  /** Whether beat 1 is accented (warm tint + halo). */
  accent: boolean;
  /** Dot diameter in px for the *normal* beats, beat 1 is rendered slightly larger when accented. */
  size?: number;
  /** Horizontal gap between dots in px. */
  gap?: number;
}

/**
 * Tiny row of dots, the discrete pulse indicator for the metronome.
 *
 * Subscribes locally to `studio.continuousMetronome.onBeat` so the parent
 * (popover, toolbar chip) doesn't re-render on every beat.
 */
export function MetronomePulseDots({
  signature,
  running,
  accent,
  size = 7,
  gap = 4,
}: MetronomePulseDotsProps) {
  const { studio } = useStudio();
  const [beat, setBeat] = useState(0);

  useEffect(() => {
    if (!studio || !running) {
      return;
    }

    const cleanupTimers = new Set<number>();

    const off = studio.continuousMetronome.onBeat((event) => {
      const delay = Math.max(0, event.realTimeMs - performance.now());
      const id = window.setTimeout(() => {
        cleanupTimers.delete(id);
        setBeat(event.beat);
      }, delay);

      cleanupTimers.add(id);
    });

    return () => {
      off();

      for (const id of cleanupTimers) {
        clearTimeout(id);
      }

      cleanupTimers.clear();
    };
  }, [studio, running]);

  // When the metronome stops, freeze the dots in their idle state. We don't
  // reset the beat counter inside the effect (cascading renders), we just
  // ignore the stale value while `running` is false.
  const displayedBeat = running ? beat : 0;

  const dots: ReactNode[] = [];

  for (let i = 0; i < signature.num; i++) {
    const isStrong = i === 0 && accent;
    const isLit = running && displayedBeat === i;
    const dotSize = isStrong ? size + 2 : size;

    let background: string;

    if (isLit) {
      background = isStrong
        ? "var(--color-accent-warm)"
        : "var(--color-accent)";
    } else if (isStrong) {
      background = "var(--color-accent-warm-subtle)";
    } else {
      background = "rgba(255, 255, 255, 0.08)";
    }

    let borderColor: string;

    if (isStrong) {
      borderColor = isLit
        ? "var(--color-accent-warm)"
        : "rgba(251, 191, 36, 0.35)";
    } else if (isLit) {
      borderColor = "var(--color-accent)";
    } else {
      borderColor = "transparent";
    }

    let boxShadow: string;

    if (isLit && isStrong) {
      boxShadow = "0 0 10px var(--color-accent-warm-glow)";
    } else if (isLit) {
      boxShadow = "0 0 8px var(--color-violet-glow)";
    } else {
      boxShadow = "none";
    }

    dots.push(
      <span
        key={i}
        aria-hidden
        style={{
          width: dotSize,
          height: dotSize,
          borderRadius: 999,
          background,
          border: `1px solid ${borderColor}`,
          boxShadow,
          transition: "background 80ms, box-shadow 80ms, border-color 80ms",
        }}
      />,
    );
  }

  return (
    <span
      className="inline-flex items-center"
      style={{ gap }}
      role="img"
      aria-label={`${signature.num} temps par mesure`}
    >
      {dots}
    </span>
  );
}
